import { useEffect, useRef, useState } from 'react';
import { confirmSolAction } from '../../data/sol/sol-confirm-client';
import { myDayNotificationService } from '../my-day/notification-service';
import type { SolMyDayProposal } from '../../data/sol/sol-contract';
import { useSolSession } from './sol-session';
import {
  ACTION_CANCELLED_TEXT,
  ACTION_CONFIRM_PROMPT,
  ACTION_EXPIRED_TEXT,
  ACTION_FAILED_TEXT,
  ACTION_SIGNED_OUT_TEXT,
  MY_DAY_ACTION_STALE_TEXT,
  myDayActionConfirmedText,
  myDayProposalHeadline,
} from './sol-wording';

/**
 * THE MY DAY CONFIRMATION CARD — WD-2.
 *
 * **NOTHING HAS HAPPENED WHEN THIS APPEARS.** Sol interpreted a request,
 * Crewlink resolved exactly one follow-up (or none, for a create) and
 * canonicalized every value; the card is the whole of what was done. The
 * write happens on Confirm, on a separate endpoint, or it never happens.
 *
 * **IT SAYS EXACTLY WHAT CONFIRM WILL DO, PER VERB, BEFORE IT ASKS** — the
 * resolved calendar date rather than "Friday", the Crewlink timezone beside a
 * time, SKIP THIS OCCURRENCE or END THE SERIES spelled out for a recurring
 * dismissal, old → new lines for an edit, and "this occurrence only" for a
 * recurring reschedule. A confirmation Brandon cannot check is one he learns
 * to tap without reading.
 *
 * The mechanics — session-settled state, the single-flight ref, expiry
 * derived at render, Cancel sending nothing — are the Capture card's,
 * deliberately unchanged.
 */

export interface SolMyDayProposalCardProps {
  readonly proposal: SolMyDayProposal;
  readonly disabled?: boolean | undefined;
  readonly nowSeconds?: (() => number) | undefined;
}

type CardState =
  | { readonly kind: 'offered' }
  | { readonly kind: 'confirming' }
  | { readonly kind: 'done'; readonly text: string }
  | { readonly kind: 'stopped'; readonly text: string };

const RECUR_LABEL: Record<string, string> = {
  daily: 'Repeats daily',
  weekdays: 'Repeats weekdays',
  weekly: 'Repeats weekly',
  every_2_weeks: 'Repeats every 2 weeks',
  monthly: 'Repeats monthly',
};

function scheduleLine(dueDate: string, dueTime: string | null, timeZone: string | null): string {
  if (dueTime === null) return dueDate;
  return timeZone === null ? `${dueDate} · ${dueTime}` : `${dueDate} · ${dueTime} (${timeZone})`;
}

export function SolMyDayProposalCard({
  proposal,
  disabled,
  nowSeconds,
}: SolMyDayProposalCardProps) {
  const session = useSolSession();
  const [state, setState] = useState<CardState>(
    () => session.settledActions.get(proposal.ticket) ?? { kind: 'offered' },
  );
  const mounted = useRef(true);
  const inFlight = useRef(false);

  const settle = (next: CardState): void => {
    if (next.kind === 'done' || next.kind === 'stopped') {
      session.settledActions.set(proposal.ticket, next);
    }
    setState(next);
  };

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const { display } = proposal;
  const now = (nowSeconds ?? (() => Math.floor(Date.now() / 1000)))();
  const expired = state.kind === 'offered' && now >= proposal.expiresAt;

  const confirm = (): void => {
    if (inFlight.current || state.kind !== 'offered' || disabled === true || expired) return;
    inFlight.current = true;
    setState({ kind: 'confirming' });

    void confirmSolAction({ ticket: proposal.ticket }).then((outcome) => {
      inFlight.current = false;
      if (!mounted.current) return;
      switch (outcome.status) {
        case 'confirmed_my_day':
          // **THE ROW THE DATABASE RETURNED** names the outcome, not the ask.
          settle({
            kind: 'done',
            text: myDayActionConfirmedText(outcome.action, outcome.item.title),
          });
          // **WD-3: THE SAME RECONCILIATION PATH AS EVERY OTHER MUTATION** —
          // Sol → proposal → Confirm → database → authoritative reread →
          // device reconciliation. Sol never schedules anything itself; this
          // re-reads server truth and lets the one reconciler converge.
          // Never prompts, never blocks the card, inert off native.
          void myDayNotificationService().run();
          return;
        case 'follow_up_changed':
        case 'capture_changed':
          settle({ kind: 'stopped', text: MY_DAY_ACTION_STALE_TEXT });
          return;
        case 'expired':
          settle({ kind: 'stopped', text: ACTION_EXPIRED_TEXT });
          return;
        case 'signed_out':
          settle({ kind: 'stopped', text: ACTION_SIGNED_OUT_TEXT });
          return;
        case 'aborted':
          setState({ kind: 'offered' });
          return;
        default:
          settle({ kind: 'stopped', text: ACTION_FAILED_TEXT });
      }
    });
  };

  if (state.kind === 'done') {
    return (
      <p className="sol-action-done" role="status">
        ✓ {state.text}
      </p>
    );
  }
  if (state.kind === 'stopped') {
    return (
      <p className="sol-action-stopped" role="alert">
        {state.text}
      </p>
    );
  }
  if (expired) {
    return (
      <p className="sol-action-stopped" role="status">
        {ACTION_EXPIRED_TEXT}
      </p>
    );
  }

  const busy = state.kind === 'confirming';
  const headline = myDayProposalHeadline(display);

  return (
    <section className="sol-action" aria-label={`Confirm follow-up change: ${display.title}`}>
      <p className="sol-action-prompt">{ACTION_CONFIRM_PROMPT}</p>
      <p className="sol-action-headline">{headline}</p>
      <p className="sol-action-location">{display.title}</p>

      {display.action === 'create' ? (
        <>
          <p className="sol-action-detail">
            {scheduleLine(display.dueDate, display.dueTime, display.timeZone)}
          </p>
          {display.description !== null ? (
            <p className="sol-action-detail">{display.description}</p>
          ) : null}
          {display.projectName !== null ? (
            <p className="sol-action-detail">Project: {display.projectName}</p>
          ) : null}
          {display.priority === 'high' ? <p className="sol-action-detail">High priority</p> : null}
          {display.recurRule !== null ? (
            <p className="sol-action-detail">
              {RECUR_LABEL[display.recurRule] ?? display.recurRule}
            </p>
          ) : null}
        </>
      ) : null}

      {display.action === 'done' ? (
        <>
          <p className="sol-action-detail">
            {scheduleLine(display.dueDate, display.dueTime, display.timeZone)}
          </p>
          {/* **A RECURRING DONE IS THIS OCCURRENCE, SAID PLAINLY** — the series
              is not ending, and the card must not let him think it is. */}
          {display.recurring ? (
            <p className="sol-action-detail">Completes this occurrence — the series continues.</p>
          ) : null}
        </>
      ) : null}

      {display.action === 'dismiss' ? (
        <p className="sol-action-detail">
          {display.mode === 'skip_occurrence'
            ? `Skips the ${display.dueDate} occurrence only. The series continues.`
            : display.mode === 'end_series'
              ? 'Ends the whole series. No future occurrences.'
              : 'Dismisses it — this does not mark it done.'}
        </p>
      ) : null}

      {display.action === 'edit' && display.changes !== null ? (
        <ul className="sol-action-changes">
          {display.changes.map((change) => (
            <li key={change.field} className="sol-action-detail">
              {/* **A CLEAR IS SHOWN AS A CLEAR** — "none", never a dash that
                  could read as "not applicable". */}
              {change.field}: {change.from ?? 'none'} → {change.to ?? 'none'}
            </li>
          ))}
        </ul>
      ) : null}

      {display.action === 'reschedule' && display.from !== null ? (
        <>
          <p className="sol-action-transition">
            <span>{scheduleLine(display.from.dueDate, display.from.dueTime, null)}</span>
            <span className="sol-action-arrow" aria-hidden="true">
              →
            </span>
            <span>{scheduleLine(display.dueDate, display.dueTime, display.timeZone)}</span>
          </p>
          {display.recurring ? (
            <p className="sol-action-detail">
              Moves this occurrence only — the series keeps its own rhythm.
            </p>
          ) : null}
        </>
      ) : null}

      <p className="sol-action-buttons">
        <button
          type="button"
          className="sol-action-confirm"
          disabled={busy || disabled === true}
          onClick={confirm}
        >
          {busy ? 'Confirming…' : 'Confirm'}
        </button>
        <button
          type="button"
          className="sol-action-cancel"
          disabled={busy}
          onClick={() => {
            // **NOTHING IS SENT.** The proposal simply expires unused.
            settle({ kind: 'stopped', text: ACTION_CANCELLED_TEXT });
          }}
        >
          Cancel
        </button>
      </p>
    </section>
  );
}
